import { INTENT_CLASSIFICATIONS } from '../../config/constants';
import { CustomIntentSchema } from './customIntent';
import { EmailAttachmentSchema } from './email';
import { GeneratedEmailBreakdownSchema } from './generatedEmailBreakdown';

type Confidence = number;
export type Classification = keyof typeof INTENT_CLASSIFICATIONS;
export type ClassificationLabel = (typeof INTENT_CLASSIFICATIONS)[Classification]['label'];

/** Default scenarios that are customizable with custom intents */
export enum DefaultIntentClassification {
  POSITIVE = 'positive_intent',
  QUALIFIED_POSITIVE = 'qualified_positive_intent_(goal_conversion)',
  QUALIFIED = 'qualified',
  NI = 'not_interested',
  NEGATIVE = 'negative_intent',
  NIN = 'not_interested_now',
  NTRP = 'not_the_right_person',
  REFERRAL = 'referral',
}

/** Check if classification string is a default scenario that's customizable with custom intents */
export function isDefaultIntent(value?: string): value is DefaultIntentClassification {
  return Object.values(DefaultIntentClassification).includes(value as DefaultIntentClassification);
}

export interface PlaygroundIntroductionEmailAPISchema {
  attachments: EmailAttachmentSchema[];
  botEmail: string;
  botName: string;
  emailBody: string;
  hasFollowUp: boolean;
  leadEmail: string;
  leadName: string;
}
export type PlaygroundResponseStreamAPISchema = PlaygroundResponseAPISchema | { data: string };
export interface PlaygroundResponseAPISchema {
  botEmail: string;
  botName: string;
  ccs: string[];
  emailBody: string;
  hasEnded: boolean;
  hasFollowUp: boolean;
  leadEmail: string;
  leadName: string;
  message: string | null;
  // TODO: Make engagementState non-optional once BE has shipped it
  engagementState?: 'pending_user_review' | null;
}
export interface PlaygroundEmailSchema {
  botEmail: string;
  botName: string;
  hasFollowUp: boolean;
  leadEmail: string;
  leadName: string;
  role: 'assistant' | 'user';
  attachments?: EmailAttachmentSchema[];
  ccs?: string[];
  confidence?: Confidence;
  customIntents?: CustomIntentSchema[];
  emailBody?: string;
  emailBreakdown?: GeneratedEmailBreakdownSchema;
  engagementState?: 'pending_user_review' | null;
  hasEnded?: boolean;
  intentClassification?: Classification;
  intentClassificationLabel?: string;
  isRfiSentToHumanReview?: boolean;
  message?: string | null;
  playgroundEngagementId?: number;
}
export interface IntentClassificationApiSchema {
  confidence: Confidence;
  intentClassification: Classification;
  intentClassificationLabel: string;
}
export interface PlaygroundReplyPayload {
  emailBody: string;
  isFollowUp: boolean;
  priorEmails: PlaygroundEmailSchema[];
  leadId?: string;
  playgroundEngagementId?: number;
}
export interface UpdateIntentClassificationPayload {
  email: PlaygroundEmailSchema;
  index: number;
  intentClassification: Classification;
  intentClassificationLabel: ClassificationLabel;
}
export interface GetPlaygroundResponsePayload {
  isFollowUp: boolean;
  confidence?: Confidence;
  emailBody?: PlaygroundEmailSchema['emailBody'];
  intentClassification?: Classification;
  priorEmails?: PlaygroundEmailSchema[];
}
export interface EmailAnalysisSchema {
  ccs: string[];
  generalizedTalkingPoints: GeneralizedTalkingPoints;
  hasEnded: boolean;
  intentClassification: Classification;
  intentClassificationLabel: string;
  isWriteEmailRequired: boolean;
  leadEmail: string;
  leadName: string;
  message: string;
  playgroundEngagementId: number;
  playgroundGenerationUuid: string;
  primaryTalkingPoints: PrimaryTalkingPoint[];
}
export interface EmailAnalysisApiSchema {
  data: {
    attributes: EmailAnalysisSchema;
    id: number;
    type: 'playground';
  };
}

interface PrimaryTalkingPoint {
  content: string;
  customIntent: { details: Omit<CustomIntentSchema, 'id'>; id: number };
  customIntentId: number;
}

interface GeneralizedTalkingPoints {
  aiAwareness: string;
  personalBadNews: string;
  personalGoodNews: string;
  trivia: string;
}

export type WriteEmailPayloadSchema = EmailAnalysisSchema & {
  emailBody: string;
  priorEmails: PlaygroundEmailSchema[];
};

export type WriteEmailResponseStreamAPISchema = { emailBody: string } | { data: string };
export interface IgnoreEmailApiSchema {
  data: {
    attributes: { emailBody: string; hasEnded: boolean; hasFollowUp: boolean };
    id: number;
    type: 'playground';
  };
}
